import React from 'react';
import { ClipboardList } from 'lucide-react';
import { SortSelect, WosTargetInput, type ReportPageConfig, rowCounts, WeeklyPlanningReportPage, benchmarkDashboardCard, ChartCard, ChartSeries, salesAheadDashboardCard, BenchmarkDetail, SalesAheadChart, DENSE_TABLE_CSS, DENSE_TD_CSS, DENSE_TH_CSS, DetailCard, FamilyDrivers, OtbByPeriodTable, ReorderDetail, ShowAllRows, Stat, StatStrip, type KpiItem } from '../kit/ui';
import { benchmarkIndex, coverCap, fmtCurrency, fmtPct, fmtWeeks, formatBenchmark, formatIndex, gatedPeerValue, indexTone, OTB_DEV_NOT_AT_COST, OTB_DEV_TOOLTIP, OTB_NOT_AT_COST, otbCoverageNoteProp, otbExclusionNote, otbPeriodSlot, otbWindowText, PEER_TEXT_NO_GROUP, PEER_TEXT_NOT_ENOUGH, peerText, retailTooltip, ALLOCATION_RULE, basisText, CellContext, coverUnit, EMPTY, fmtCover, formatPeriodLabel, isNum, isTrendCell, metricById, NO_PLAN_LOADED_TOOLTIP, NO_WEEKLY_PLAN_TOOLTIP, NOT_AT_COST_TOOLTIP, otbLines, otbThroughTitle, resolveTarget, rowOnBasis, targetBasisNote, trendBasisLabel, trendOf, v2HistoryNote, valueOnBasis, totalsNoun, totalsViewOf, chainChildRows, FlatRow, otbSpanText, otbStartFor, benchmarkForRows, BenchmarkView, chartRowsByClass, chartRowsByClassWithPeers, kpiLabel, kpiTotals, ownIn, peerIn, peerMarkerNote, salesTrend, storeLevelReorders, withPeers, otbPlanEnd, salesAhead, salesAheadPeriods, allocatedOtbOf, allocatedOtbTooltip, bomInventoryLabel, isVendorLevelView, VENDOR_LEVEL_OTB_NOTE, wosTargetNeed, unitize } from '../kit/logic';
import type { WeeklyPlanningReportModel } from '../kit/useWeeklyPlanningReport';
import ReportModal from '../ReportModal.component';
import { AnyRow, ClassRow, Grain, isVendorRow, OtbBasis, RowAllocation } from '../../../interfaces/reports/WeeklyPlanning.interface';
import { cn } from '../../../lib/utils';
// Weekly Action's expanded-row panel (spec 7.3 "Expanded row: summary, breakdown by location, sales by
// week, inflows by week. All values are real."). Everything comes from the row's own payload data:
// the Summary from `row.summary` and the target-period cell; the two charts from `row.periods` over
// the lookback plus the target period (a missing cell is a gap, never a guess). The location
// breakdown shows only in the Location view: there the same class sits under each location, so the
// panel lists every row of the expandAll tree (`totalsRows`) with this row's class code, one per
// location. Other views show no breakdown rather than an invented split.
// Sales Trend: when the target period is a trend period the Summary adds its Sales
// Trend (the share elapsed and the basis as the note), and the sales chart adds a "Sales Trend" line on
// the trend periods; both read `trendOf(cell, ctx.trendMethod)`. The bars stay the actual sales.
// Cost basis: every dollar in the panel is the table's own cell (`metricById(id).cell`), so it follows the
// basis exactly as the columns do ("—" where there is no cost figure). Sales are retail only: the sales chart says so at cost.
// The inflow chart reads inflow and plan inflow on the basis (a gap where there is no cost).
//: about half the old height. The Summary is one stat strip (BOM inventory · On
// order · Inflows to date · WOS / MOS vs target · OTB · OTB + Dev · Trended sales) whose long explanations sit in each stat's info
// tooltip; OTB by week / month stops at the plan end with one "Later" row; the OTB table, the two charts, "Sales ahead: plan vs LY"
// (kit/logic/salesAhead.ts) and the Benchmark card sit side by side and wrap when narrow; the child drivers, the location breakdown
// and the allocation are dense tables that fold long lists behind "Show all".


export const otbNote = (row: AnyRow, basis: OtbBasis, coveredThrough: number | null, ctx: CellContext): string | null => {
  switch (basis) {
    case 'weekly_plan': return 'Planned inflows from the weekly plan.';
    case 'weekly_plan_partial':
      // D4: no monthly fill; the OTB covers the weekly plan's weeks only. item 7: a parent lists each
      // planned child's own end (otbThroughTitle, the OTB cell's own tooltip).
      if ((row.summary?.otbCoverage ?? []).some((c) => c.key !== row.key)) return otbThroughTitle(row, ctx) ?? null;
      return coveredThrough !== null
        ? `OTB through ${formatPeriodLabel(coveredThrough, ctx.grain)}: the weekly plan ends before the span does, and on order due after ${formatPeriodLabel(coveredThrough, ctx.grain)} isn't counted.`
        : 'The weekly plan covers part of the span.';
    case 'monthly_plan': return 'Planned inflows from the monthly plan.';
    case 'cost_unavailable': return NOT_AT_COST_TOOLTIP;
    case 'receipts_unavailable': return 'Needs receiving data.';
    case 'mixed': return 'The rows use different plan sources.';
    default: return null;
  }
};

const SERIES = (unit: string): ChartSeries[] => [
  { key: 'actual', label: `Actual ${unit}`, kind: 'bar', color: '#9E77ED' },
  { key: 'plan', label: 'Plan', kind: 'line', color: '#101828', bold: true },
];
const TREND_SERIES: ChartSeries = { key: 'trend', label: 'Trended sales', kind: 'line', color: '#F79009' };
/** The detail's small fixed-size charts (fixed so they line up in one row and render under jsdom). */
export const DETAIL_CHART_WIDTH = 250;
export const DETAIL_CHART_HEIGHT = 130;

/** The table's text for a column (the same value, basis and "—" rules as the cell). */
const cellText = (id: string, row: AnyRow, ctx: CellContext): string => metricById(id).cell(row, ctx).text;

const periodsOf = (ctx: CellContext): number[] => Array.from(new Set([...ctx.lookback, ctx.targetPeriod])).sort((a, b) => a - b);

/**
 * The sales / inflows chart data per period: actual, plan (a gap where the plan is null) and, in Monthly, a `planNote` on a month whose plan
 * is the V2 plan as it stood (D6: "Plan as it stood in Sep 2026 (V2)"), which the chart tooltip shows. The plan line is drawn through those months.
 */
export const detailChartData = (row: AnyRow, ctx: CellContext, periods: number[], actual: 'sales' | 'inflow', plan: 'salesPlan' | 'inflowPlan') =>
  periods.map((p) => {
    const c = row.periods[String(p)];
    const planNote = ctx.grain === 'monthly' && c?.planV2History ? { planNote: v2HistoryNote(p) } : {};
    if (actual === 'sales') return { name: formatPeriodLabel(p, ctx.grain), actual: c?.sales ?? null, plan: c?.salesPlan ?? null, trend: trendOf(c, ctx.trendMethod).salesTrend, ...planNote };
    return { name: formatPeriodLabel(p, ctx.grain), actual: valueOnBasis(c, actual, ctx.basis).value, plan: valueOnBasis(c, plan, ctx.basis).value, ...planNote };
  });

/** "WOS vs target" value: the cover against its resolved target (a child judged at its parent shows the cover alone). */
export const coverVsTarget = (row: AnyRow, ctx: CellContext): Stat => {
  const s = row.summary;
  const { target, basis } = resolveTarget(s, ctx.wosOverride);
  return {
    id: 'cover', label: `${coverUnit(ctx.grain)} vs target`,
    value: s ? (basis === 'parent' ? fmtWeeks(s.wos, coverCap(ctx.grain)) : fmtCover(s.wos, target, coverCap(ctx.grain))) : EMPTY,
    info: s ? targetBasisNote(basis, s) : null,
  };
};

/** The OTB stat's explanation: no plan, not at cost, or the OTB basis sentence (otbNote). */
const otbInfo = (row: AnyRow, ctx: CellContext): string | null => {
  const s = row.summary;
  if (!s) return null;
  if (s.planBasis === 'none_weekly') return NO_WEEKLY_PLAN_TOOLTIP;
  if (s.planBasis === 'none_plan') return NO_PLAN_LOADED_TOOLTIP;
  const otb = metricById('otb').cell(row, ctx);
  if (otb.title === NOT_AT_COST_TOOLTIP) return NOT_AT_COST_TOOLTIP;
  // Wendi (2026-10-22), item 3: an allocated OTB states its basis.
  const allocated = allocatedOtbOf(row, ctx);
  return [allocated ? allocatedOtbTooltip(allocated, ctx.grain) : null, otbNote(row, s.otbBasis, s.otbCoveredThrough, ctx)].filter(Boolean).join(' ') || null;
};

/** The Trended sales stat (only when the target period is a trend period): the value, with the share elapsed and the basis in its tooltip. */
export const trendedSalesStat = (row: AnyRow, ctx: CellContext): Stat | null => {
  const t = trendOf(row.periods[String(ctx.targetPeriod)], ctx.trendMethod);
  if (!t.trendBasis) return null;
  return { id: 'trend', label: 'Trended sales', value: cellText('salesTrend', row, ctx), info: `${fmtPct(t.trendPct)} of period elapsed · ${trendBasisLabel(t.trendBasis, t.trendNote)}` };
};

/**
 * The Summary strip: BOM inventory · On order · Inflows to date · WOS / MOS vs target · OTB · OTB + Dev · Trended sales.
 * a stat with no figure ("—", e.g. OTB for a class with no weekly plan) is left out; the headline says why there is none.
 */
export const summaryStats = (row: AnyRow, ctx: CellContext, opts: { vendorLevel?: boolean } = {}): Stat[] => {
  const trend = trendedSalesStat(row, ctx);
  // Item 13 (Wendi, 2026-10-22): at vendor level (no class above the row) the OTB block gives way to the WOS-target need.
  const need = opts.vendorLevel ? wosTargetNeed(row, ctx) : null;
  const otbStats: Stat[] = opts.vendorLevel
    ? [{ id: 'need', label: `Need (${coverUnit(ctx.grain)} target)`, value: ctx.basis === 'cost' ? EMPTY : fmtCurrency(need), info: unitize(`${VENDOR_LEVEL_OTB_NOTE}. ${ALLOCATION_RULE}`, ctx.grain) }]
    : [
      { id: 'otb', label: 'OTB', value: metricById('otb').cell(row, ctx).text, info: otbInfo(row, ctx) },
      { id: 'otbDev', label: 'OTB + Dev', value: cellText('otbDev', row, ctx), info: unitize(OTB_DEV_TOOLTIP, ctx.grain) },
    ];
  return ([
    { id: 'inventory', label: bomInventoryLabel(ctx.grain), value: cellText('inventory', row, ctx), info: metricById('inventory').cell(row, ctx).title ?? null },
    { id: 'onOrder', label: 'On order', value: cellText('onOrder', row, ctx) },
    { id: 'received', label: 'Inflows to date', value: basisText(rowOnBasis(row, 'receivedToDate', ctx), fmtCurrency).text },
    coverVsTarget(row, ctx),
    ...otbStats,
    ...(trend ? [trend] : []),
  ] as Stat[]).filter((st) => st.value !== EMPTY);
};

/**
 * the detail opens on the row's action and the facts behind it, in one line: cover against target,
 * on order, open to buy, and why a figure is missing (no weekly plan / no plan loaded).
 */
export const actionHeadline = (row: AnyRow, ctx: CellContext, opts: { vendorLevel?: boolean } = {}): { action: string; facts: string[]; why: string | null } => {
  const s = row.summary;
  const actionCell = metricById('action').cell(row, ctx);
  const facts: string[] = [];
  const cover = coverVsTarget(row, ctx);
  if (cover.value !== EMPTY) facts.push(`${coverUnit(ctx.grain)} ${cover.value}`);
  const onOrder = cellText('onOrder', row, ctx);
  if (onOrder !== EMPTY && (s?.onOrder ?? 0) > 0) facts.push(`${onOrder} on order`);
  const otbDev = cellText('otbDev', row, ctx);
  if (otbDev !== EMPTY && !opts.vendorLevel) facts.push(`OTB + Dev ${otbDev}`);
  if (opts.vendorLevel) facts.push(unitize(VENDOR_LEVEL_OTB_NOTE, ctx.grain));
  // an overstock with on order and a negative OTB + Dev says how much of that on order to delay or cancel.
  const code = actionCell.value;
  const otbDevNow = s?.otbDev;
  const onOrderNow = s?.onOrder;
  if ((code === 'sell_down' || code === 'push_orders') && isNum(otbDevNow) && otbDevNow < 0 && isNum(onOrderNow) && onOrderNow > 0) {
    facts.push(`On order exceeds OTB + Dev by ${fmtCurrency(Math.min(onOrderNow, -otbDevNow))}: delay or cancel that much`);
  }
  if (s?.planBasis === 'none_weekly') facts.push('no weekly plan, so no OTB');
  else if (s?.planBasis === 'none_plan') facts.push('no plan loaded, so no OTB');
  // A child class with no plan of its own is judged at its parent: say which one instead of "see parent".
  if (s?.planParentLabel && /see parent/i.test(actionCell.text)) {
    return { action: `Judged at its parent, ${s.planParentLabel}`, facts, why: `This class has no plan of its own: its status, action and OTB are its parent's (${s.planParentLabel}).` };
  }
  return { action: actionCell.text, facts, why: actionCell.title ?? null };
};

/** The headline row: the action (as the table shows it) and its facts; the action's explanation is the tooltip. */
const ActionHeadline: React.FC<{ row: AnyRow; ctx: CellContext; vendorLevel?: boolean }> = ({ row, ctx, vendorLevel }) => {
  const h = actionHeadline(row, ctx, { vendorLevel });
  if (h.action === EMPTY && h.facts.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]" title={h.why ?? undefined}>
      {h.action !== EMPTY && <span className="font-bold text-[#101828]">{h.action}</span>}
      {h.facts.length > 0 && <span className="text-[#475467]">{h.action !== EMPTY ? '· ' : ''}{h.facts.join(' · ')}</span>}
    </div>
  );
};

/** A chart's series that have data: a Plan line with no plan figure anywhere (no weekly plan) is dropped, and so is its legend entry. */
const seriesWithData = (series: ChartSeries[], data: Record<string, unknown>[]): ChartSeries[] =>
  series.filter((sr) => sr.key === 'actual' || data.some((d) => typeof d[sr.key] === 'number'));

const th = (text: string, left = false) => <th className={cn(DENSE_TH_CSS, left ? 'text-left pr-3' : 'text-right pl-3')}>{text}</th>;
const td = (text: React.ReactNode, left = false, extra = '') => <td className={cn(DENSE_TD_CSS, left ? 'text-left pr-3 whitespace-nowrap' : 'text-right pl-3', extra)}>{text}</td>;

/**
 * W2 (D12): the class's allocation ("open to sell", retail): its stores ranked by need, what each is sent and from where, the
 * non-selling locations' stock and what is left, and the shortfall or surplus (kit/logic/allocation.ts). The rule is the heading's tooltip.
 */
export const AllocationDetail: React.FC<{ allocation: RowAllocation; id: string; grain: Grain }> = ({ allocation, id, grain }) => {
  const b = allocation.balance;
  const balance = b === null ? null : b > 0.005 ? `Shortfall ${fmtCurrency(b)}: to buy / chase` : b < -0.005 ? `Surplus ${fmtCurrency(-b)}: hold in the warehouse` : 'Balanced';
  return (
    <DetailCard title={`Allocation this ${grain === 'monthly' ? 'month' : 'week'} (retail)`} headingId={`allocation-${id}`} info={unitize(ALLOCATION_RULE, grain)}>
      {allocation.stores.length > 0 && (
        <table aria-labelledby={`allocation-${id}`} className={DENSE_TABLE_CSS}>
          <thead>
            <tr>{th('Store', true)}{th('Need')}{th('Send')}<th className={cn(DENSE_TH_CSS, 'text-left pl-3')}>From</th></tr>
          </thead>
          <tbody>
            <ShowAllRows
              items={allocation.stores}
              colSpan={4}
              render={(st) => (
                <tr key={st.key}>
                  {td(st.label, true)}
                  {td(fmtCurrency(st.need))}
                  {td(st.allocate > 0 ? fmtCurrency(st.allocate) : EMPTY)}
                  <td className={cn(DENSE_TD_CSS, 'text-left pl-3 whitespace-nowrap')}>{st.from.length ? st.from.join(', ') : EMPTY}</td>
                </tr>
              )}
            />
          </tbody>
        </table>
      )}
      {allocation.sources.length > 0 && (
        <table aria-label="Available at non-selling locations" className={DENSE_TABLE_CSS}>
          <thead>
            <tr>{th('Non-selling location', true)}{th('Available')}{th('Left after sending')}</tr>
          </thead>
          <tbody>
            <ShowAllRows
              items={allocation.sources}
              colSpan={3}
              render={(src) => (
                <tr key={src.key}>{td(src.label, true)}{td(fmtCurrency(src.available))}{td(fmtCurrency(src.available - src.allocated))}</tr>
              )}
            />
          </tbody>
        </table>
      )}
      {balance && <p className="text-[11px] font-semibold text-[#344054]">{balance}</p>}
    </DetailCard>
  );
};

export interface WeeklyActionRowDetailProps {
  model: WeeklyPlanningReportModel;
  flatRow: FlatRow;
}

export const WeeklyActionRowDetail: React.FC<WeeklyActionRowDetailProps> = ({ model, flatRow }) => {
  const { ctx } = model;
  if (!ctx) return null;
  const { row } = flatRow;
  const s = row.summary;
  const unit = ctx.grain === 'weekly' ? 'week' : 'month';
  const periods = periodsOf(ctx);
  // Sales are retail on every basis (no sales at cost); inflow and its plan follow the basis.
  const series = (actual: 'sales' | 'inflow', plan: 'salesPlan' | 'inflowPlan') => detailChartData(row, ctx, periods, actual, plan);
  const cost = ctx.basis === 'cost';
  const hasTrend = periods.some((p) => isTrendCell(row.periods[String(p)]));

  // / OTB span follows the grain: OTB by week (Weekly) or by month (Monthly) and running OTB (retail: there
  // is no per-period figure at cost).
  const otbPeriods = cost ? null : otbLines(s);
  const salesData = series('sales', 'salesPlan');
  const inflowData = series('inflow', 'inflowPlan');
  const ahead = salesAhead([row], salesAheadPeriods(ctx.targetPeriod, model.payload?.otbPeriods ?? model.payload?.spanPeriods), ctx.grain);

  const inLocationView = model.view.state.view === 'locationCategoryClass' && !isVendorRow(row) && !!row.classCode;
  const locations = inLocationView
    ? model.totalsRows.filter((f) => (f.kind === 'leaf' || f.kind === 'group') && !isVendorRow(f.row) && f.row.classCode === row.classCode)
    : [];
  const cover = coverUnit(ctx.grain);
  // Item 13 (Wendi, 2026-10-22): Total by Vendor, Vendor by Class, Vendor by Product have no class level above the row.
  const vendorLevel = isVendorLevelView(model.view.state.view);

  return (
    <div className="flex flex-col gap-3 py-2">
      <ActionHeadline row={row} ctx={ctx} vendorLevel={vendorLevel} />
      <StatStrip label="Summary" stats={summaryStats(row, ctx, { vendorLevel })} />
      <div className="flex flex-wrap items-stretch gap-3">
        {otbPeriods && !vendorLevel && <OtbByPeriodTable id={flatRow.id} lines={otbPeriods} grain={ctx.grain} planEnd={otbPlanEnd(s, otbPeriods)} />}
        <ChartCard dense title={`Sales by ${unit}${cost ? ' (retail)' : ''}`} data={salesData} series={seriesWithData(hasTrend ? [...SERIES('sales'), TREND_SERIES] : SERIES('sales'), salesData)} xKey="name" formatValue={fmtCurrency} width={DETAIL_CHART_WIDTH} height={DETAIL_CHART_HEIGHT} />
        <ChartCard dense title={`Inflows by ${unit}${cost ? ' (cost)' : ''}`} data={inflowData} series={seriesWithData(SERIES('inflows'), inflowData)} xKey="name" formatValue={fmtCurrency} width={DETAIL_CHART_WIDTH} height={DETAIL_CHART_HEIGHT} />
        {/* "Plan vs LY" needs a plan: a class with no plan ahead shows no such chart (the headline says there is no plan). */}
        {ahead.data.some((d) => d.plan !== null) && <SalesAheadChart dense ahead={ahead} grain={ctx.grain} width={DETAIL_CHART_WIDTH} height={DETAIL_CHART_HEIGHT} />}
        {/* the class's benchmark metrics against its master class (nothing for vendor / product rows, below class level or at none). */}
        <BenchmarkDetail row={row} ctx={ctx} />
      </div>
      <div className="flex flex-wrap items-start gap-3 empty:hidden">
        {/* Item 7 (Wendi, 2026-10-22): the figures behind a product's suggested reorder. */}
        <ReorderDetail row={row} ctx={ctx} id={flatRow.id} />
        <FamilyDrivers row={row} rows={row.key.startsWith('chain:') ? chainChildRows(model.payload?.rows ?? [], ctx, model.view.state.filters.locations, row.key) : model.payload?.rows ?? []} ctx={ctx} id={flatRow.id} />
        {locations.length > 0 && (
          <DetailCard title="By location" headingId={`by-location-${flatRow.id}`}>
            <table aria-labelledby={`by-location-${flatRow.id}`} className={DENSE_TABLE_CSS}>
              <thead>
                <tr>{th('Location', true)}{th(bomInventoryLabel(ctx.grain))}{th(cover)}{th('Sales')}{th('OTB')}</tr>
              </thead>
              <tbody>
                <ShowAllRows
                  items={locations}
                  colSpan={5}
                  render={(f) => {
                    const r = f.row as ClassRow;
                    return (
                      <tr key={f.id}>
                        {td(r.locationName ?? r.locationCode ?? EMPTY, true)}
                        {td(cellText('inventory', r, ctx))}
                        {td(r.summary ? fmtCover(r.summary.wos, resolveTarget(r.summary, ctx.wosOverride).target) : EMPTY)}
                        {td(cellText('thisPeriodSales', r, ctx))}
                        {td(cellText('otb', r, ctx))}
                      </tr>
                    );
                  }}
                />
              </tbody>
            </table>
          </DetailCard>
        )}
        {row.allocation && (row.allocation.stores.length > 0 || row.allocation.sources.length > 0) && (
          <AllocationDetail allocation={row.allocation} id={flatRow.id} grain={ctx.grain} />
        )}
      </div>
    </div>
  );
};



// Weekly Action's page config (Plan 4 spec 7.3). A module-level constant: the page shell
// memoises on its identity. KPIs and charts read `model.totalsRows` (the expandAll tree), so they
// follow the filters and search but not row expansion. The WOS target writes `state.wosOverride`,
// which re-derives Health / Action / urgency client-side (it is not a query parameter).
// Sales Trend KPI: the value counts classes with the trend period
// valued at its Sales Trend (`salesTrend` in pageModel); when the target is a trend period the
// sub-line is the same count on sales so far ("Actual so far ↑ a · ↓ b") and the tooltip adds the
// sales so far, the Sales Trend dollars and the share of the period elapsed.
// Classes to Reorder: understocked + out of stock only; a Review row (out of stock, no sales in
// the lookback or the last 52 weeks) is not a reorder and is counted on the sub-line as "N to review".
// OTB + Dev sits beside Total OTB, summed over the same counted rows (totalRows); the
// PNOTB / NOTB exclusion is noted once, on the Total OTB tooltip.
// Benchmarks: none of the four KPIs measures a benchmark metric, so a fifth KPI "Weeks of stock vs peers"
// carries the client's benchmark WOS against the vertical's (G4), with sell-through on its benchmark line. It reads
// `ctx.benchmarks.client` only (the class rows and the benchmark payload, never SKU / product rows), so it never moves while
// those load; it is absent at entitlement 'none' and "not enough peers" when the pool cannot be published. The cover chart
// gets the G3 "Peers" marker (class level, class-first views); OTB by class is dollars of plan and gets none; the dashboard ends
// with "You vs peers". Benchmark ratios are retail-based, so they show on either cost basis and say "(retail)" at cost. Your value is summed over
// the rows the report counts (benchmarkForRows), the client level where those cannot give it; Monthly reads months of supply (MOS).
// Cost basis: Total OTB is the OTB at cost (Monthly; "—" with one note when the plan has no inflow at
// cost, e.g. every Weekly plan); OTB + Dev is never at cost (plans have no BOM at cost). The OTB chart follows the basis.

const coverUnitOf = (m: WeeklyPlanningReportModel): 'WOS' | 'MOS' => (m.view.state.grain === 'monthly' ? 'MOS' : 'WOS');

/**
 * the client's benchmark weeks of stock vs the vertical's, with sell-through on the benchmark line; null without a client value.
 * Your value is summed over the rows the report counts (`benchmarkForRows`: the same classes as the KPIs beside it); where the rows cannot give
 * it (a vendor or product view) it is the client-level figure and the sub-line and tooltip say so. Monthly shows months of supply (MOS).
 */
export const benchmarkKpi = (m: WeeklyPlanningReportModel): KpiItem | null => {
  const { ctx } = m;
  const b = ctx?.benchmarks;
  const r = ctx ? benchmarkForRows(m.totalsRows, ctx) : null;
  if (!ctx || !b || !r) return null;
  const c = r.value;
  const { grain, basis } = ctx;
  const view: BenchmarkView = { grain, weeksInMonth: b.weeksInMonth, basis };
  const peerWos = gatedPeerValue(c.peer, 'weeksOfStock');
  const idx = benchmarkIndex('weeksOfStock', c.own.weeksOfStock, peerWos);
  const clientLevel = r.scope === 'client';
  const filtered = clientLevel && c.filtered;
  const sub = c.status === 'ok' ? `index ${formatIndex('weeksOfStock', idx)}${clientLevel ? ' · client level' : ''}` : c.peer ? PEER_TEXT_NOT_ENOUGH : PEER_TEXT_NO_GROUP;
  const cover = grain === 'monthly' ? 'Months of stock' : 'Weeks of stock';
  return {
    label: kpiLabel(`${cover} vs peers`, { filtered, basis }),
    value: `${ownIn('weeksOfStock', c.own.weeksOfStock, view)} vs ${peerWos === null ? '—' : peerIn(c.peer, 'weeksOfStock', view)}`,
    sub,
    tone: indexTone('weeksOfStock', idx) === 'bad' ? 'warn' : 'neutral',
    tooltip: retailTooltip(`Benchmark ${grain === 'monthly' ? 'months of stock' : 'weeks of stock'}: month-end inventory ÷ that month's sales${grain === 'monthly' ? '' : ' per week'}, ${c.peerLabel}. Not the report's ${coverUnitOf(m)} (start-of-period inventory ÷ lookback average). ${clientLevel ? 'Client level: every class passing the scope filters, not only the rows shown.' : "Your value covers the same classes as the other KPIs."}`, basis),
    benchmark: { text: `Sell-through${grain === 'weekly' ? ' (monthly)' : ''} you ${formatBenchmark('sellThrough', c.own.sellThrough)} · peers ${peerText(c.peer, 'sellThrough')} · ${b.label ?? ''}` },
  };
};

// The one OTB KPI, with a "Dev" switch (saved in the view, `kpiOtbIncludeDev`): on (the default), OTB + Dev = OTB + (plan BOM − actual
// BOM); off, the OTB itself (planned inflows − received − on order). It replaced the separate "Open to buy" card: the span and basis are on
// the sub line, the OTB's exclusion and plan-coverage notes in the tooltip either way. The table's OTB + Dev column and statuses do not change.
const otbKpi = (m: WeeklyPlanningReportModel, t: ReturnType<typeof kpiTotals>, grain: Grain): KpiItem => {
  const { state, set } = m.view;
  const include = state.kpiOtbIncludeDev;
  const cost = state.basis === 'cost';
  // with a later start the sub line names the window the payload counted ("Jan W1–Mar W4") instead of the span.
  const window = otbStartFor(state.otbStart, grain, m.payload?.targetPeriod ?? m.currentPeriod) !== null ? otbWindowText(m.payload?.spanPeriods, grain) : null;
  const span = `${window ?? otbSpanText(state)}${cost ? ' · cost' : ''}`;
  const dev = t.otbDev !== null && t.otb !== null ? t.otbDev - t.otb : null;
  const otbTip = `Open to buy: planned inflows over the span − received to date − on order${cost ? ', every figure at cost (the plan\'s own inflows at cost; weekly plans carry none)' : ''}. ${otbExclusionNote(t.otbExcluded)}`;
  const common = { toggle: { label: 'Dev', checked: include, onChange: (kpiOtbIncludeDev: boolean) => set({ kpiOtbIncludeDev }) }, ...otbCoverageNoteProp(t.otbPartial, m.payload?.spanPeriods, grain) };
  if (include) {
    return {
      label: 'OTB + Dev', value: fmtCurrency(t.otbDev),
      sub: cost ? OTB_DEV_NOT_AT_COST : dev === null ? `${span} · OTB + (plan BOM − actual BOM)` : `${span} · Dev ${fmtCurrency(dev)} added`,
      tooltip: `${OTB_DEV_TOOLTIP} ${otbTip} Switch Dev off to show the OTB without the deviation; the table and statuses still use OTB + Dev. Saved views keep the choice.`,
      ...common,
    };
  }
  return {
    label: 'OTB', value: fmtCurrency(t.otb),
    sub: cost && t.notAtCost.includes('otb') ? OTB_NOT_AT_COST : dev === null ? `${span} · Dev left out` : `${span} · Dev ${fmtCurrency(dev)} left out`,
    tooltip: `${otbTip} Switch Dev on to add the deviation (plan BOM − actual BOM). Saved views keep the choice.`,
    ...common,
  };
};

const kpis = (m: WeeklyPlanningReportModel): KpiItem[] => {
  const { ctx } = m;
  if (!ctx) return [];
  const { state } = m.view;
  const t = kpiTotals(m.totalsRows, ctx);
  const trend = salesTrend(m.totalsRows, ctx);
  // the rows whose action is a reorder (Reorder, Reorder · no OTB); understocked rows held back (on order
  // covers it, out of season, a no-action class type) are not counted.
  const reorder = t.reorder;
  const held = t.byAction.on_order_covers + t.byAction.out_of_season;
  const review = t.byStatus.review;
  // Vendor by Class counts vendor × class rows, Total by Vendor vendors, the others classes (totalsNoun).
  const noun = totalsNoun(state.view, 2);
  const one = totalsNoun(state.view, 1);
  const n = ctx.lookback.length;
  const periods = `${n} ${ctx.grain === 'weekly' ? (n === 1 ? 'week' : 'weeks') : n === 1 ? 'month' : 'months'}`;
  const trendText = `over the last ${periods} against plan (their own average where there is no plan)`;
  const Noun = `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;
  // In a trend period the sub-line becomes "Actual so far", so the tooltip carries the counted noun and window.
  // the trend-period tooltip also states the comparison rule (Sales Trend, not sales so far; retail at any basis).
  const tooltip = trend.trendPeriod
    ? `${Noun} ${trendText}, the period still in progress counted at its trended sales: ahead of plan (▲) or behind it (▼). The sub-line counts the same ${noun} on sales so far. Compare does not change it. Sales so far ${fmtCurrency(t.salesRetail)} · Trended sales ${fmtCurrency(t.salesTrend)} (retail; ${fmtPct(t.trendPct)} of the period elapsed). In the period still in progress it is the trended sales (sales carried to the period end) that are compared, not the sales so far; plans are retail, so the comparison is retail at any basis.`
    : `${Noun} whose sales ${trendText} are ahead of plan (▲) or behind it (▼). Compare does not change it.`;
  const bench = benchmarkKpi(m);
  // Round 2: a chain KPI that reads 0 can hide understocked stores; the sub-line names how many store-level rows are reorders.
  const storeReorders = reorder === 0 && state.view !== 'locationCategoryClass' && m.payload ? storeLevelReorders(m.payload.rows, ctx) : 0;
  // Round 2: "of N classes" is the caption's own count (chain classes after the merge), so the header and this KPI never disagree.
  const ofRows = totalsViewOf(state.view) === 'totalByVendor' || totalsViewOf(state.view) === 'vendorByClass' ? t.rows : rowCounts(m.totalsRows, m.payload, m.vendorPayload, state.view).shown;
  // the sub-line reads "of N · X held (on order / season) · Y to review"; the store-level count moves into the tooltip.
  const storeNote = storeReorders > 0 ? ` ${storeReorders} store-level ${storeReorders === 1 ? 'reorder' : 'reorders'} in Location view.` : '';
  return [
    {
      label: 'Classes to Reorder', value: String(reorder), sub: `of ${ofRows} ${ofRows === 1 ? one : noun}${held > 0 ? ` · ${held} held (on order / season)` : ''}${review > 0 ? ` · ${review} to review` : ''}`, tone: reorder > 0 ? 'bad' : 'neutral',
      tooltip: `Rows whose action is Reorder or Reorder · no OTB. Understocked rows whose on order already covers the gap, that are out of season, or whose class type takes no buy (PNOTB, NOTB, History, Distress, Warehouse) are not counted.${storeNote}`,
    },
    otbKpi(m, t, ctx.grain),
    { label: 'Urgent reorders', value: String(t.urgent), sub: 'on order will not reach target', tone: t.urgent > 0 ? 'bad' : 'neutral', tooltip: 'A reorder whose on order due within the span still leaves cover under target.' },
    {
      label: `${Noun} vs plan ▲/▼`,
      value: `▲ ${trend.above} · ▼ ${trend.below}`,
      sub: trend.trendPeriod ? `Sales so far ▲ ${trend.actual.above} · ▼ ${trend.actual.below}` : `${noun} ${trendText}`,
      tone: trend.above > trend.below ? 'good' : trend.below > trend.above ? 'bad' : 'neutral',
      tooltip,
    },
    ...(bench ? [bench] : []),
  ];
};

const OTB_SERIES: ChartSeries[] = [{ key: 'value', label: 'OTB', kind: 'bar', color: '#9E77ED' }];
const coverSeries = (unit: string): ChartSeries[] => [
  { key: 'value', label: unit, kind: 'bar', color: '#9E77ED' },
  { key: 'plan', label: 'Target', kind: 'line', color: '#101828', bold: true },
];

const dashboardCards = (m: WeeklyPlanningReportModel): React.ReactNode[] => {
  const { ctx } = m;
  if (!ctx) return [];
  const unit = coverUnitOf(m);
  return [
    <ChartCard
      key="cover"
      title={`${unit} by class vs plan`}
      caption="Bars: current cover · line: target"
      data={chartRowsByClassWithPeers(m.totalsRows, ctx, 'cover', m.view.state.view, 'weeksOfStock')}
      series={withPeers(coverSeries(unit), ctx, m.view.state.view)}
      note={peerMarkerNote(ctx, m.view.state.view) ?? undefined}
      xKey="name"
      sortable
      formatValue={fmtWeeks}
      cap={coverCap(m.view.state.grain)}
    />,
    <ChartCard key="otb" title="OTB by class" caption={`Open to buy over the span${m.view.state.basis === 'cost' ? ', at cost' : ''}`} data={chartRowsByClass(m.totalsRows, ctx, 'otb', m.view.state.view)} series={OTB_SERIES} xKey="name" sortable formatValue={fmtCurrency} />,
    // plan sales ahead vs LY over the counted classes (kit/summary/salesAheadCard.tsx).
    salesAheadDashboardCard(m),
    benchmarkDashboardCard(m, ['weeksOfStock', 'sellThrough', 'turnover', 'salesChangeLy']),
  ].filter((card) => card !== null);
};

export const WEEKLY_ACTION_CONFIG: ReportPageConfig = {
  reportKey: 'weekly_action',
  // Wendi (2026-10-22), item 9: "Inventory Action" everywhere user-facing; the key `weekly_action`, routes, saved-view keys and file names stay.
  title: 'Inventory Action',
  subtitle: "What to reorder, delay or cancel, or sell down this period, against each class's plan.", //
  icon: <ClipboardList className="w-5 h-5" aria-hidden="true" />,
  howItWorksIntro:
    //: the action labels ("Delay or cancel orders", "No action"); codes unchanged.
    'Inventory Action tells you what to reorder, delay or cancel, or sell down this period. For each class it compares the weeks (or months) of stock you have with the target from your plan, and your BOM inventory with planned inventory. ' +
    'Understocked classes get Reorder, unless the class is out of season, on order already covers the gap, or its class type takes no buy (shown as "—"). "Reorder · no OTB" means there is no open to buy left (OTB + Dev ≤ 0). ' +
    'Overstocked classes get "Sell down · delay or cancel orders", or "Delay or cancel orders" when inventory is close to plan and a lot is still on order: delay (push out) the delivery dates or cancel them. ' +
    '"No action" means the class is on plan: nothing to buy, cancel or sell down this period. "No action · plan steps down" means at or below plan BOM, but next period\'s plan steps down: sell through current stock rather than reorder. ' +
    // short sentences, no history note.
    'Stock with no sales in the lookback reads No recent sales. It is never urgent and never a reorder. Its action is "Review · clearance" when you hold more than plan inventory (or have no plan inventory). At or below plan inventory it is No action, never cleared. ' +
    'A Deadstock tag marks a class or product with more than a year of supply at its current sell rate that is also selling less than last year; filter on it from Status. ' +
    'Rows open sorted by Action priority (urgent reorders, reorders, reorders with no OTB, sell-downs, then the rest; ties by weeks of stock ÷ target). Sort also offers Most overstocked first (weeks of stock ÷ target, highest first), or click a column header. Saved views keep their own sort and columns. ' +
    'Type your own WOS target to re-judge every class without reloading, and open a row to see its summary, sales and inflows by period. ' +
    'While the period is still in progress, Sales WTD / MTD shows the sales so far (compared with the plan prorated for the days elapsed), and Trended sales and % of period elapsed show where sales are heading (Trended is compared with the full plan). A Tier C client has no sales so far: its figure is the month projection, tagged projected. ' +
    'Needs Attention lists only classes that are out of stock, understocked, overstocked, without recent sales or without a plan; On track and Watch classes are never listed. An overstock reason names the rule behind it: weeks of stock against target, or BOM above plan BOM × the multiplier. ' +
    'OTB and OTB + Dev follow Action in the default columns; when a partial weekly plan makes Weekly OTB cover less than the span, the OTB card says so (Monthly OTB uses the monthly plan). ' +
    'Benchmarks (optional columns, the "Weeks of stock vs peers" KPI and the dashboard) compare you with peers for one complete month; see Benchmarks below.',
  kpis,
  dashboardCards,
  // / the optional "OTB wk of Oct 18", … / "OTB Nov", … columns exist for the span's periods only (a 13-week span has thirteen).
  columnsFilter: (m, ids) => ids.filter((id) => { const slot = otbPeriodSlot(id); return slot === null || slot <= (m.payload?.otbPeriods?.length ?? 0); }),
  extraViewControls: (m) => {
    const unit = coverUnitOf(m);
    return (
      <>
        <WosTargetInput
          label={`${unit} target`}
          unit={unit === 'WOS' ? 'wks' : 'mos'}
          value={m.view.state.wosOverride}
          onChange={(wosOverride) => m.view.set({ wosOverride })}
        />
        <SortSelect grain={m.view.state.grain} value={m.view.state.sort} onChange={(sort) => m.view.set({ sort })} />
      </>
    );
  },
  renderRowDetail: (m, flatRow) => <WeeklyActionRowDetail model={m} flatRow={flatRow} />,
};

// Weekly Action (spec 7.3), rebuilt on the kit (Plan 4 ): the shared page shell with
// this report's config. The exported names and props are the ones the two mounting pages use
// (ClientAnalytics, ClientDashboardV3Reports).

export interface WeeklyActionReportProps {
  clientId: string | number;
  epochMonth: number | null;
  setEpochMonth?: (epochMonth: number) => void;
}

export const WeeklyActionReport: React.FC<WeeklyActionReportProps> = (props) => <WeeklyPlanningReportPage config={WEEKLY_ACTION_CONFIG} {...props} />;

export interface WeeklyActionReportModalProps {
  clientId: string | number;
  epochMonth: number | null;
  onClose: () => void;
}

export const WeeklyActionReportModal: React.FC<WeeklyActionReportModalProps> = ({ clientId, epochMonth, onClose }) => (
  <ReportModal onClose={onClose}>
    <WeeklyActionReport clientId={clientId} epochMonth={epochMonth} />
  </ReportModal>
);

export default WeeklyActionReport;
